import { axiosClient } from "./axios-client";
import { api } from "./endpoints";

// Raw shape from the admin API (backend/src/models/Blog.js).
type BlogRecord = {
  _id: string;
  company: string;
  title: string;
  slug: string;
  coverImage: string;
  excerpt: string;
  content: string;
  category: string;
  author: string;
  status: "draft" | "published";
  featured: boolean;
  publishedDate: string;
  createdAt: string;
  updatedAt: string;
};

type ListEnvelope<T> = {
  success: boolean;
  data: T[];
  meta: { total: number; page: number; limit: number };
};

type ItemEnvelope<T> = {
  success: boolean;
  data: T;
};

// This is the Exec9 site, so blogs are always scoped to Exec9. The backend list endpoint returns
// records for ALL companies when no company is passed, so the scope is explicit here — otherwise
// devEncodes posts would surface on exec9.com. Records tagged `all` are included server-side.
const COMPANY = "exec9";

export type BlogSummary = {
  slug: string;
  title: string;
  excerpt: string;
  coverImage: string;
  category: string;
  author: string;
  publishedDate: string;
  readMinutes: number;
};

export type BlogDetail = BlogSummary & {
  /** Admin-authored sanitized HTML from the rich-text editor. */
  content: string;
};

// Rough reading time from the rendered text length of the HTML body (~200 wpm).
function readMinutes(html: string): number {
  const text = html.replace(/<[^>]+>/g, " ");
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

function toSummary(record: BlogRecord): BlogSummary {
  return {
    slug: record.slug,
    title: record.title,
    excerpt: record.excerpt,
    coverImage: record.coverImage,
    category: record.category || "Blog",
    author: record.author || "Exec9 Team",
    publishedDate: record.publishedDate,
    readMinutes: readMinutes(record.content),
  };
}

async function fetchBlogRecords(): Promise<BlogRecord[]> {
  try {
    const envelope = (await axiosClient.get(api.getBlogs, {
      params: { company: COMPANY, status: "published", limit: 1000 },
    })) as unknown as ListEnvelope<BlogRecord>;
    return envelope.data;
  } catch {
    // The backend being unreachable must degrade to an empty list, never throw — this runs in
    // server components / generateStaticParams where an uncaught throw takes down the build.
    return [];
  }
}

export const getBlogs = async (): Promise<BlogSummary[]> => {
  const records = await fetchBlogRecords();
  return records
    .filter((r) => r.status === "published")
    .sort((a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime())
    .map(toSummary);
};

export const getBlogBySlug = async (slug: string): Promise<BlogDetail | null> => {
  try {
    const envelope = (await axiosClient.get(`${api.getBlogs}/slug/${slug}`, {
      params: { company: COMPANY },
    })) as unknown as ItemEnvelope<BlogRecord>;
    const record = envelope.data;
    if (record.status !== "published") return null;
    return { ...toSummary(record), content: record.content };
  } catch {
    return null;
  }
};
